import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'

interface BottomCTAProps {
  buildLoginRedirect: (target: string) => string
}

export default function BottomCTA({ buildLoginRedirect }: BottomCTAProps) {
  return (
    <section className="w-full bg-[rgb(var(--bg))] px-4 py-[var(--section-py-sm)] sm:px-6 md:py-[var(--section-py)] lg:px-8">
      <div className="mx-auto" style={{ maxWidth: 'var(--container-max)' }}>
        <div 
          className="relative overflow-hidden text-center text-[rgb(var(--hero-text))] px-6 py-12 sm:px-10 sm:py-16 md:px-14 md:py-24"
          style={{
            borderRadius: 'var(--r-xl)',
            border: `1px solid rgb(var(--stroke))`,
            background: 'rgb(var(--hero-bg))',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          {/* Subtle pattern & depth effects */}
          <div className="pointer-events-none absolute inset-0">
            {/* 그라데이션 오버레이 */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/10" />
            {/* 미세한 패턴 효과 */}
            <div 
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                backgroundSize: '24px 24px'
              }}
            />
            {/* 글로우 효과 */}
            <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-white/15 blur-3xl" />
            <div className="absolute -right-24 -bottom-24 h-72 w-72 rounded-full bg-black/15 blur-3xl" />
          </div>

          <div className="relative z-10">
            {/* 헤드라인 - 2줄 고정 */}
            <h2 
              className="mx-auto max-w-3xl text-2xl sm:text-3xl md:text-4xl lg:text-[48px]"
              style={{
                fontWeight: 'var(--fw-bold)',
                lineHeight: 'var(--lh-tight)',
                letterSpacing: 'var(--ls-tight)',
                marginBottom: 'var(--section-title-gap)',
              }}
            >
              프리랜서 프로필을 등록하고
              <br />
              프리랜서 풀에 먼저 참여하세요
            </h2>

            {/* 보조 문구 */}
            <p 
              className="mx-auto max-w-2xl text-sm sm:text-base"
              style={{
                color: 'rgb(var(--hero-muted))',
                marginBottom: 'var(--section-content-gap)',
              }}
            >
              프로필 등록 후 맞는 프로젝트 알림을 받고 제안을 시작할 수 있어요
            </p>

            {/* CTA 버튼 */}
            <div className="flex flex-col items-center justify-center gap-3">
              <Button
                asChild
                className="w-full max-w-[320px] whitespace-nowrap px-4 text-center text-sm leading-none transition-all focus-visible:outline-none sm:text-base"
                style={{
                  height: 'var(--btn-h-lg)',
                  borderRadius: 'var(--r-lg)',
                  backgroundColor: 'var(--btn-primary-bg)',
                  color: 'var(--btn-primary-fg)',
                  paddingLeft: 'var(--btn-px)',
                  paddingRight: 'var(--btn-px)',
                  fontWeight: 'var(--fw-semi)',
                  boxShadow: 'var(--shadow-md)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--btn-primary-hover)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--btn-primary-bg)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)'
                }}
              >
                <Link
                  href={buildLoginRedirect('/onboarding/freelancer/role')}
                  data-landing-event="click_cta_bottom_primary"
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap"
                >
                  프리랜서 프로필 등록하기
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
              
              {/* 작은 주석 */}
              <p 
                className="text-xs sm:text-sm"
                style={{
                  color: 'rgb(var(--hero-muted))',
                  opacity: 0.8,
                }}
              >
                프리랜서 풀 등록·프로필 작성·제안 제출 무료
              </p>
            </div>

          </div>
        </div>
      </div>
    </section>
  )
}
